import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, ShoppingCart, Heart, User, LogOut, Plus, Menu, X, MapPin, Star, Package, Gift, Filter,
  Upload, Check, AlertCircle, Trash2, Eye, HelpCircle, ChevronRight, FileText, Shield, Mail,
  MapPinned, RefreshCw, BookOpen, ArrowLeftRight, Lock, Unlock, Users, Calendar, Award, Sparkles,
  Home, History, Bookmark, Settings, Info, ChevronDown, AlertTriangle, Truck, Download
} from "lucide-react";
import { axios, API, BACKEND_URL, logError } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import {
  PriceDisplay, StatusBadge, TransactionTypeBadge, AgeBadge, KidTip, TierBadge
} from "../components/Badges";
import { TrustScoreBadge } from "../components/TrustScoreBadge";
import { useExperiment } from "../hooks/useExperiment";
import { ParentalGate } from "../components/ParentalGate";
import { ShippingCheckoutModal } from "../components/ShippingCheckoutModal";
import { ShippingLabelControls } from "../components/ShippingLabelControls";
import { ToyCycleLogo } from "../components/Logo";
import { ToyCard, SuccessPopup, getImageUrl } from "../components/ToyCard";
import { FreeToysStrip } from "../components/FreeToysStrip";
import { HighlightsStrip } from "../components/HighlightsStrip";
import { HeroSection } from "../components/HeroSection";
import { StatsStrip } from "../components/StatsStrip";

export const MarketplacePage = () => {
  const { user, token } = useAuth();
  const [listings, setListings] = useState({ recommended: [], sell: [], swap: [], donate: [] });
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // A/B: landing-page hero copy. Defaults match the rebuilt hero so the page
  // is never blank while the assignment request is in flight.
  const heroExp = useExperiment("landing_hero_cta_v1", {
    headline: "Give every toy a second chance.",
    subheadline: "Buy, sell, swap, or donate pre-loved toys with families across the UK — safely, sustainably, and for a fraction of retail.",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [sellRes, swapRes, donateRes] = await Promise.all([
          axios.get(`${API}/listings?transaction_type=sell&limit=8`),
          axios.get(`${API}/listings?transaction_type=swap&limit=8`),
          axios.get(`${API}/listings?transaction_type=donate&limit=8`)
        ]);
        let recommended = [];
        if (token) {
          try {
            const recRes = await axios.get(`${API}/listings/recommended`, { headers });
            recommended = recRes.data;
          } catch { recommended = sellRes.data.slice(0, 4); }
          try {
            const savedRes = await axios.get(`${API}/saved-items`, { headers });
            setSavedItems(savedRes.data.map(l => l.id));
          } catch (err) { logError("Failed to load saved items", err); }
        }
        setListings({ recommended, sell: sellRes.data, swap: swapRes.data, donate: donateRes.data });
      } catch (err) { logError("Failed to load marketplace", err); } finally { setLoading(false); }
    };
    fetchData();
  }, [token]);

  const handleSave = async (listingId) => {
    if (!token) { toast.error("Please sign in to save items"); return; }
    try {
      if (savedItems.includes(listingId)) {
        await axios.delete(`${API}/saved-items/${listingId}`, { headers: { Authorization: `Bearer ${token}` } });
        setSavedItems(prev => prev.filter(id => id !== listingId));
        toast.success("Removed from saved items");
      } else {
        await axios.post(`${API}/saved-items`, { listing_id: listingId }, { headers: { Authorization: `Bearer ${token}` } });
        setSavedItems(prev => [...prev, listingId]);
        toast.success("Saved to your collection!");
      }
    } catch { toast.error("Failed to update saved items"); }
  };

  const renderSection = (title, items, icon, link, color) => (
    <section className="py-12">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
          <span className={`w-10 h-10 ${color} rounded-full flex items-center justify-center text-white border-2 border-gray-900 shadow-[3px_3px_0px_0px_rgba(17,24,39,1)]`}>{icon}</span>
          {title}
        </h2>
        <Link to={link} className="brutalist-btn-secondary text-sm py-2 px-4">View All</Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map(listing => <ToyCard key={listing.id} listing={listing} onSave={handleSave} isSaved={savedItems.includes(listing.id)} />)}
      </div>
    </section>
  );

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-12"><div className="animate-pulse grid grid-cols-4 gap-6">{Array.from({length: 8}, (_, i) => `sk-${i}`).map((k) => <div key={k} className="brutalist-card"><div className="aspect-square bg-gray-200" /><div className="p-4 space-y-3"><div className="h-4 bg-gray-200 rounded w-3/4" /></div></div>)}</div></div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="marketplace-page">
      {/* Hero (real photography + dual CTAs + trust badges) */}
      <HeroSection
        headline={heroExp.payload.headline}
        subheadline={heroExp.payload.subheadline}
        isLoggedIn={Boolean(user)}
        experimentVariant={heroExp.variant}
      />

      {/* Live impact counters */}
      <StatsStrip />

      {/* Marketplace highlights — paid promotions + price drops + relists */}
      <HighlightsStrip />

      {/* Free Toys Today — engagement strip (above the fold) */}
      <FreeToysStrip />

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: <ShoppingCart className="w-6 h-6" />, label: "Buy Toys", link: "/browse?transaction_type=sell", color: "bg-blue-500", testid: "quick-buy" },
          { icon: <Package className="w-6 h-6" />, label: "Sell a Toy", link: user ? "/create-listing" : "/auth", color: "bg-[#FF6B6B]", testid: "quick-sell" },
          { icon: <ArrowLeftRight className="w-6 h-6" />, label: "Swap Toys", link: "/browse?transaction_type=swap", color: "bg-purple-500", testid: "quick-swap" },
          { icon: <Gift className="w-6 h-6" />, label: "Donate / Free", link: "/browse?is_donation=true", color: "bg-green-500", testid: "quick-donate" }
        ].map((item, i) => (
          <Link key={item.testid} to={item.link} data-testid={item.testid} className="brutalist-card p-4 flex flex-col items-center gap-2 hover:scale-105 transition-transform">
            <div className={`w-12 h-12 ${item.color} rounded-full flex items-center justify-center text-white border-2 border-gray-900 shadow-[3px_3px_0px_0px_rgba(17,24,39,1)]`}>{item.icon}</div>
            <span className="font-bold text-sm">{item.label}</span>
          </Link>
        ))}
      </div>

      {/* Recommended Section */}
      {user && listings.recommended.length > 0 && renderSection("Recommended For You", listings.recommended, <Sparkles className="w-5 h-5" />, "/browse", "bg-[#FF6B6B]")}

      {/* Buy Section */}
      {listings.sell.length > 0 && renderSection("Toys to Buy", listings.sell, <ShoppingCart className="w-5 h-5" />, "/browse?transaction_type=sell", "bg-blue-500")}

      {/* Swap Section */}
      {listings.swap.length > 0 && renderSection("Toys to Swap", listings.swap, <ArrowLeftRight className="w-5 h-5" />, "/browse?transaction_type=swap", "bg-purple-500")}

      {/* Donate Section */}
      {listings.donate.length > 0 && renderSection("Free Donations", listings.donate, <Gift className="w-5 h-5" />, "/browse?is_donation=true", "bg-green-500")}
    </div>
  );
};

// ============== ENHANCED DASHBOARD ==============
